import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Search, User, Bell, CheckCircle, XCircle, AlertTriangle, Loader, Eye, EyeOff, Download, Shield, FileText } from 'lucide-react';
import Card3D from './Card3D';
import { studentAPI } from '../api/api';

const StudentDashboard: React.FC = () => {
  const [studentRecord, setStudentRecord] = useState<any>(null);
  const [notifications, setNotifications] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [rollNumber, setRollNumber] = useState('');
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [privacySettings, setPrivacySettings] = useState({
    hide_cgpa: false,
    hide_backlogs: false,
    hide_contact: false,
    hide_remarks: false
  });

  // Load student record
  const loadStudentRecord = useCallback(async () => {
    if (!rollNumber.trim()) return;
    
    try {
      setLoading(true);
      setError('');
      
      const data: any = await studentAPI.view(rollNumber.toLowerCase());
      setStudentRecord(data.record);
      setNotifications(data.record.student_notifications || []);
      setIsLoggedIn(true);
      
    } catch (err: any) {
      setError(err.message);
      setStudentRecord(null);
      setIsLoggedIn(false);
    } finally {
      setLoading(false);
    }
  }, [rollNumber]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    await loadStudentRecord();
  };

  const handleReportMistake = async (rollNumber: string, description: string) => {
    try {
      await studentAPI.reportMistake(rollNumber, { mistake_description: description });
      await loadStudentRecord(); // Refresh data
      setSuccess('✅ Mistake reported to institution successfully!');
    } catch (err: any) {
      setError(err.message);
      throw err;
    }
  };

  const downloadRecord = () => {
    if (!studentRecord) return;
    
    const recordData = {
      ...studentRecord,
      // Apply privacy settings
      cgpa: privacySettings.hide_cgpa ? 'HIDDEN' : studentRecord.cgpa,
      backlogs: privacySettings.hide_backlogs ? 'HIDDEN' : studentRecord.backlogs,
      gmail: privacySettings.hide_contact ? 'HIDDEN' : studentRecord.gmail,
      phone_number: privacySettings.hide_contact ? 'HIDDEN' : studentRecord.phone_number,
      remarks: privacySettings.hide_remarks ? 'HIDDEN' : studentRecord.remarks
    };
    
    const dataStr = JSON.stringify(recordData, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,' + encodeURIComponent(dataStr);
    
    const exportFileDefaultName = `academic_record_${studentRecord.roll_number}.json`;
    
    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();
  };

  const getNotificationIcon = (type: string) => {
    const icons = {
      record_updated: <CheckCircle className="h-4 w-4 text-neon-blue" />,
      record_deleted: <XCircle className="h-4 w-4 text-red-400" />,
      record_restored: <CheckCircle className="h-4 w-4 text-neon-green" />,
      verification_approved: <Shield className="h-4 w-4 text-neon-purple" />,
      mistake_reported: <AlertTriangle className="h-4 w-4 text-yellow-400" />
    };
    return icons[type as keyof typeof icons] || <Bell className="h-4 w-4 text-gray-400" />;
  };

  const getNotificationColor = (priority: string) => {
    const colors = {
      high: 'border-red-400/50 bg-red-400/10',
      normal: 'border-neon-blue/50 bg-neon-blue/10',
      low: 'border-gray-400/50 bg-gray-400/10'
    };
    return colors[priority as keyof typeof colors] || colors.normal;
  };

  if (!isLoggedIn) {
    return (
      <motion.div 
        className="min-h-screen p-8 flex items-center justify-center"
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
      >
        <div className="max-w-md w-full">
          <motion.div 
            className="glass-card p-8 rounded-xl text-center"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2, duration: 0.6 }}
          >
            <motion.div
              className="p-4 bg-gradient-to-br from-neon-green/20 to-green-600/20 rounded-full w-20 h-20 mx-auto mb-6 flex items-center justify-center"
              animate={{ 
                boxShadow: [
                  "0 0 0 0 rgba(0, 255, 136, 0.4)",
                  "0 0 0 20px rgba(0, 255, 136, 0)",
                  "0 0 0 0 rgba(0, 255, 136, 0)"
                ]
              }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              <User className="h-10 w-10 text-neon-green" />
            </motion.div>
            
            <h1 className="text-4xl font-bold bg-gradient-to-r from-neon-green to-green-400 bg-clip-text text-transparent mb-4">
              👨‍🎓 Student Portal
            </h1>
            <p className="text-gray-300 mb-8">Access your academic records and manage privacy settings</p>
            
            {error && (
              <motion.div 
                className="alert-error p-3 rounded-lg flex items-center space-x-2 mb-6 text-sm"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
              >
                <XCircle className="h-4 w-4 text-red-400" />
                <span>{error}</span>
              </motion.div>
            )}
            
            <form onSubmit={handleLogin} className="space-y-6">
              <input
                type="text"
                value={rollNumber}
                onChange={(e) => setRollNumber(e.target.value)}
                className="w-full px-4 py-3 rounded-lg text-white bg-dark-card/80 border border-neon-green/30 focus:border-neon-green focus:outline-none text-center font-mono tracking-wider"
                placeholder="Enter your Roll Number"
                required
                disabled={loading}
              />
              
              <motion.button
                type="submit"
                disabled={loading}
                className="w-full py-3 bg-gradient-to-r from-neon-green to-green-600 hover:from-green-600 hover:to-neon-green rounded-lg font-semibold text-white disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300"
                whileHover={{ scale: 1.02, y: -2 }}
                whileTap={{ scale: 0.98 }}
              >
                {loading ? (
                  <Loader className="animate-spin h-5 w-5 mx-auto" />
                ) : (
                  <>
                    <Search className="h-5 w-5 mr-2 inline" />
                    View My Record
                  </>
                )}
              </motion.button>
            </form>
            
            <p className="text-xs text-gray-400 mt-4">
              Enter your roll number to access your academic transcript and notifications
            </p>
          </motion.div>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div 
      className="min-h-screen p-8"
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8 }}
    >
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div 
          className="flex justify-between items-center mb-12"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <div>
            <h1 className="text-6xl font-bold bg-gradient-to-r from-neon-green to-green-400 bg-clip-text text-transparent mb-2">
              👨‍🎓 Student Portal
            </h1>
            <p className="text-2xl text-gray-300">
              Welcome, {studentRecord?.full_name || 'Student'}
            </p>
            <p className="text-neon-green font-mono text-lg tracking-wider">
              {studentRecord?.roll_number?.toUpperCase()}
            </p>
          </div>
          
          <div className="flex items-center space-x-4">
            {/* Notifications */}
            <motion.button
              onClick={() => setShowNotifications(!showNotifications)}
              className="relative p-3 glass-card rounded-lg hover:bg-yellow-400/10 transition-colors"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Bell className="h-6 w-6 text-yellow-400" />
              {notifications.filter(n => !n.read).length > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                  {notifications.filter(n => !n.read).length}
                </span>
              )}
            </motion.button>
            
            {/* Logout */}
            <motion.button
              onClick={() => {
                setIsLoggedIn(false);
                setStudentRecord(null);
                setRollNumber('');
                setError('');
                setSuccess('');
              }}
              className="px-4 py-2 bg-red-500/20 hover:bg-red-500/30 rounded-lg text-red-400 font-medium transition-colors"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              Logout
            </motion.button>
          </div>
        </motion.div>

        {/* Success/Error Messages */}
        {error && (
          <motion.div 
            className="alert-error p-4 rounded-lg flex items-center space-x-3 mb-6"
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
          >
            <XCircle className="h-5 w-5 text-red-400" />
            <span>{error}</span>
          </motion.div>
        )}

        {success && (
          <motion.div 
            className="alert-success p-4 rounded-lg flex items-center space-x-3 mb-6"
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
          >
            <CheckCircle className="h-5 w-5 text-green-400" />
            <span>{success}</span>
          </motion.div>
        )}

        {/* Notifications Panel */}
        {showNotifications && (
          <motion.div 
            className="glass-card p-6 rounded-xl mb-8"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold text-white flex items-center">
                <Bell className="h-5 w-5 mr-2 text-yellow-400" />
                Notifications ({notifications.length})
              </h3>
              <button
                onClick={() => setShowNotifications(false)}
                className="text-gray-400 hover:text-white"
              >
                ✕
              </button>
            </div>
            
            {notifications.length === 0 ? (
              <p className="text-gray-400 text-center py-4">No notifications yet</p>
            ) : (
              <div className="space-y-3 max-h-64 overflow-y-auto">
                {notifications.slice(0, 10).map((notification, index) => (
                  <motion.div 
                    key={notification.id || index}
                    className={`p-3 rounded-lg border ${getNotificationColor(notification.priority)} ${!notification.read ? 'font-medium' : 'opacity-70'}`}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.05 }}
                  >
                    <div className="flex items-start space-x-3">
                      {getNotificationIcon(notification.type)}
                      <div className="flex-1 min-w-0">
                        <p className="text-white text-sm break-words">{notification.message}</p>
                        <p className="text-xs text-gray-400 mt-1">
                          {new Date(notification.timestamp).toLocaleString()}
                        </p>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </motion.div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Academic Record Card */}
          <div className="lg:col-span-2">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3, duration: 0.6 }}
            >
              <h2 className="text-2xl font-semibold text-white mb-6 flex items-center">
                <FileText className="h-6 w-6 mr-2 text-neon-blue" />
                My Academic Record
              </h2>
              
              {studentRecord && (
                <Card3D
                  record={{
                    ...studentRecord,
                    // Apply privacy settings
                    cgpa: privacySettings.hide_cgpa ? 'Hidden by Privacy Settings' : studentRecord.cgpa,
                    backlogs: privacySettings.hide_backlogs ? 'Hidden by Privacy Settings' : studentRecord.backlogs,
                    gmail: privacySettings.hide_contact ? 'Hidden by Privacy Settings' : studentRecord.gmail,
                    phone_number: privacySettings.hide_contact ? 'Hidden by Privacy Settings' : studentRecord.phone_number,
                    remarks: privacySettings.hide_remarks ? 'Hidden by Privacy Settings' : studentRecord.remarks
                  }}
                  onReportMistake={handleReportMistake}
                  type="student"
                />
              )}
            </motion.div>
          </div>

          {/* Privacy Controls & Actions */}
          <div className="space-y-6">
            {/* Privacy Settings */}
            <motion.div 
              className="glass-card p-6 rounded-xl"
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.4, duration: 0.6 }}
            >
              <h3 className="text-xl font-semibold text-white mb-4 flex items-center">
                <Shield className="h-5 w-5 mr-2 text-neon-purple" />
                Privacy Control
              </h3>
              <p className="text-gray-300 text-sm mb-4">
                Control what information you share through selective disclosure
              </p>
              
              <div className="space-y-3">
                {[
                  { key: 'hide_cgpa', label: 'Hide CGPA', icon: '📊' },
                  { key: 'hide_backlogs', label: 'Hide Backlogs', icon: '⚠️' },
                  { key: 'hide_contact', label: 'Hide Contact Info', icon: '📧' },
                  { key: 'hide_remarks', label: 'Hide Remarks', icon: '📝' }
                ].map((setting) => (
                  <label key={setting.key} className="flex items-center space-x-3 cursor-pointer group">
                    <input
                      type="checkbox"
                      checked={privacySettings[setting.key as keyof typeof privacySettings]}
                      onChange={(e) => setPrivacySettings({
                        ...privacySettings,
                        [setting.key]: e.target.checked
                      })}
                      className="w-4 h-4 text-neon-purple bg-dark-card border-gray-600 rounded focus:ring-neon-purple focus:ring-2"
                    />
                    <span className="text-sm text-gray-300 group-hover:text-white transition-colors">
                      {setting.icon} {setting.label}
                    </span>
                    {privacySettings[setting.key as keyof typeof privacySettings] ? 
                      <EyeOff className="h-4 w-4 text-red-400" /> : 
                      <Eye className="h-4 w-4 text-neon-green" />
                    }
                  </label>
                ))}
              </div>
              
              <div className="mt-4 p-3 bg-neon-purple/10 border border-neon-purple/30 rounded-lg">
                <p className="text-xs text-neon-purple">
                  🔒 Hidden fields will show "Hidden by Privacy Settings" in downloads and verifications
                </p>
              </div>
            </motion.div>

            {/* Actions */}
            <motion.div 
              className="glass-card p-6 rounded-xl"
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.5, duration: 0.6 }}
            >
              <h3 className="text-xl font-semibold text-white mb-4">Quick Actions</h3>
              
              <div className="space-y-3">
                <motion.button
                  onClick={downloadRecord}
                  className="w-full p-3 bg-gradient-to-r from-neon-blue to-blue-600 hover:from-blue-600 hover:to-neon-blue rounded-lg text-white font-medium transition-all duration-300 flex items-center justify-center space-x-2"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <Download className="h-4 w-4" />
                  <span>Download Record</span>
                </motion.button>
                
                <motion.button
                  onClick={loadStudentRecord}
                  disabled={loading}
                  className="w-full p-3 bg-gradient-to-r from-gray-600 to-gray-700 hover:from-gray-500 hover:to-gray-600 rounded-lg text-white font-medium transition-all duration-300 flex items-center justify-center space-x-2 disabled:opacity-50"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  {loading ? (
                    <Loader className="animate-spin h-4 w-4" />
                  ) : (
                    <>
                      <Search className="h-4 w-4" />
                      <span>Refresh Data</span>
                    </>
                  )}
                </motion.button>
              </div>
            </motion.div>

            {/* Record Status */}
            <motion.div 
              className="glass-card p-6 rounded-xl"
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.6, duration: 0.6 }}
            >
              <h3 className="text-xl font-semibold text-white mb-4">Record Status</h3>
              
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-gray-300">Status:</span>
                  <span className={`px-2 py-1 rounded-full text-xs font-bold ${
                    studentRecord?.status === 'active' ? 'bg-neon-green/20 text-neon-green' :
                    studentRecord?.status === 'deleted' ? 'bg-red-400/20 text-red-400' :
                    'bg-gray-400/20 text-gray-400'
                  }`}>
                    {(studentRecord?.status || 'Unknown').toUpperCase()}
                  </span>
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="text-gray-300">Last Updated:</span>
                  <span className="text-neon-blue text-sm">
                    {studentRecord?.updated_at ? 
                      new Date(studentRecord.updated_at).toLocaleDateString() : 
                      'N/A'
                    }
                  </span>
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="text-gray-300">Notifications:</span>
                  <span className="text-yellow-400 text-sm">
                    {notifications.filter(n => !n.read).length} Unread
                  </span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default StudentDashboard;
